"use client";

import * as React from "react";
import type { Row, Table } from "@tanstack/react-table";
import { cn } from "@aio/platform-ui";
import type { ColumnDef } from "../columns";
import { resolveAccessor } from "../columns";
import type { FormatterBundle } from "../lib/format";
import { formatCell } from "../lib/format";
import { SelectionCell } from "./selection-cell";

/**
 * Map a column's `align` prop to Tailwind text-align classes. Numeric types
 * default to right alignment unless the column explicitly overrides.
 */
function cellAlignClass<TRow>(column: ColumnDef<TRow>): string {
  const align =
    column.align ??
    (column.type === "number" ||
    column.type === "money" ||
    column.type === "percent"
      ? "right"
      : "left");
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return "text-left";
}

/**
 * Props for {@link DataRow}.
 */
export interface DataRowProps<TRow> {
  /** TanStack row instance. */
  row: Row<TRow>;
  /** Owning table — needed for selection ranges. */
  table: Table<TRow>;
  /**
   * Platform column definitions. The row uses these directly so it can call
   * the {@link formatCell} dispatcher with each column's declared type. The
   * order here is the rendering order; visibility is read from TanStack via
   * `row.getVisibleCells()` and joined to this list by `column.key`.
   */
  columns: ColumnDef<TRow>[];
  /** Locale forwarded to {@link formatCell}. */
  locale: string;
  /** Formatter bundle forwarded to {@link formatCell}. */
  formatters: FormatterBundle;
  /**
   * Called when the user activates this row by clicking anywhere except
   * inside the selection cell, or by pressing Enter while the row has
   * focus. Omit to render the row as a non-interactive list item.
   */
  onActivate?: (row: TRow) => void;
  /** Hide the selection cell (rare; e.g. read-only tables). */
  hideSelection?: boolean;
  /** Extra classes for the `<tr>`. */
  className?: string;
}

/**
 * One `<tr>` for a single dataset row.
 *
 * Layout:
 *   [ selection cell ] [ data cell 1 ] [ data cell 2 ] ... [ data cell N ]
 *
 * Behaviour:
 * - The leading {@link SelectionCell} owns its own click handling and stops
 *   propagation, so checkbox clicks never trigger {@link onActivate}.
 * - Clicking on any other cell calls `onActivate(rowOriginal)`.
 * - When `onActivate` is provided, the row is keyboard-focusable
 *   (`tabIndex=0`) and Enter triggers activation, matching the keyboard nav
 *   contract from `useKeyboardNav`.
 * - Selected rows render with `aria-selected="true"` and a `data-state`
 *   attribute so callers can target them via CSS without prop drilling.
 *
 * Cells render via {@link formatCell} unless the column declares its own
 * `cell` renderer, in which case the column owns the ReactNode directly.
 */
export function DataRow<TRow>({
  row,
  table,
  columns,
  locale,
  formatters,
  onActivate,
  hideSelection = false,
  className,
}: DataRowProps<TRow>): React.JSX.Element {
  const isSelected = row.getIsSelected();
  // Look up which platform column matches each visible TanStack cell. The
  // join key is the TanStack column id (== platform column.key by
  // convention). This lets the engine respect runtime column visibility
  // changes without forcing the caller to re-thread the column array.
  const visibleCells = row.getVisibleCells();
  const platformByKey = React.useMemo(() => {
    const map = new Map<string, ColumnDef<TRow>>();
    for (const c of columns) map.set(c.key, c);
    return map;
  }, [columns]);

  const handleRowClick = React.useCallback(() => {
    onActivate?.(row.original);
  }, [onActivate, row]);

  const handleRowKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (!onActivate) return;
      if (event.key === "Enter") {
        // Don't intercept when the focus is inside an editable element
        // within the row (matches useKeyboardNav's ignore rule).
        const target = event.target as HTMLElement;
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
        event.preventDefault();
        onActivate(row.original);
      }
    },
    [onActivate, row],
  );

  return (
    <tr
      className={cn(
        "border-b border-border outline-none",
        "transition-colors duration-fast ease-standard",
        // Hover: only meaningful when the row is interactive.
        onActivate
          ? "cursor-pointer hover:bg-bg-subtle"
          : undefined,
        // Selected rows take a stronger background that survives hover.
        isSelected ? "bg-muted hover:bg-muted" : undefined,
        // Focus ring when the row is keyboard-focused.
        onActivate
          ? "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          : undefined,
        className,
      )}
      aria-selected={isSelected}
      data-state={isSelected ? "selected" : undefined}
      tabIndex={onActivate ? 0 : -1}
      onClick={onActivate ? handleRowClick : undefined}
      onKeyDown={onActivate ? handleRowKeyDown : undefined}
      data-testid={`data-row-${row.id}`}
    >
      {!hideSelection ? <SelectionCell row={row} table={table} /> : null}
      {visibleCells.map((cell) => {
        const platform = platformByKey.get(cell.column.id);
        const value = platform
          ? resolveAccessor(platform)(row.original)
          : cell.getValue();
        const align = platform ? cellAlignClass(platform) : "text-left";
        const display = platform
          ? platform.cell
            ? platform.cell({ row: row.original, value, column: platform })
            : formatCell(value, platform, locale, formatters, {
                row: row.original,
              })
          : String(value ?? "");
        return (
          <td
            key={cell.id}
            className={cn("px-3 py-2 align-middle text-sm text-fg", align)}
            data-testid={`data-cell-${row.id}-${cell.column.id}`}
          >
            {display}
          </td>
        );
      })}
    </tr>
  );
}
